/**
 * Super Admin — Revenue:
 *  /superadmin/revenue                 Overview tiles (today/week/month/all-time)
 *  /superadmin/revenue/transactions    Transactions log
 *  /superadmin/revenue/payouts         Trainer payouts (mark paid)
 *  /superadmin/revenue/mrr             MRR + ARPU + Run-rate
 */
import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { SectionHeader } from "./SuperadminApp";
import { Card, TableShell, Th, Td, FilterPills, EmptyState, fmtINR, relTime, GhostBtn, PrimaryBtn, StatusBadge, SummaryTiles } from "./_atoms";

// ───────────────────────────────────────────────────────────────────
export function RevenueOverview() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/revenue/overview").then((r) => setData(r.data));
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 60_000); return () => clearInterval(id); }, [load]);

  const block = (label, period) => (
    <Card data-testid={`rev-${label.toLowerCase()}`}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-tertiary, #8A8884)", letterSpacing: "0.08em" }}>{label}</div>
      <div style={{ fontFamily: "Barlow Condensed", fontSize: 36, fontWeight: 900, marginTop: 4 }}>{fmtINR(period?.total)}</div>
      <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 6 }}>
        Marketplace {fmtINR(period?.marketplace)} · Sessions {fmtINR(period?.sessions)}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", marginTop: 2 }}>
        Commission earned: <strong>{fmtINR(period?.marketplace_commission)}</strong>
      </div>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="revenue-overview">
      <SectionHeader title="Revenue Overview" onRefresh={load} />
      {!data ? <EmptyState title="Loading…" /> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
          {block("Today", data.today)}
          {block("Last 7 Days", data.week)}
          {block("Last 30 Days", data.month)}
          {block("All Time", data.all_time)}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function RevenueTransactions() {
  const [data, setData] = useState({ marketplace: [], sessions: [] });
  const [tab, setTab] = useState("marketplace");
  const [status, setStatus] = useState("");
  const load = useCallback(() => {
    apiClient.get("/superadmin/revenue/transactions", { params: { status: status || undefined, limit: 100 } })
      .then((r) => setData(r.data || { marketplace: [], sessions: [] }));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const rows = tab === "marketplace" ? data.marketplace : data.sessions;
  return (
    <div className="space-y-4" data-testid="revenue-transactions">
      <SectionHeader title="Transactions" onRefresh={load} />
      <Card style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <FilterPills
          value={tab} onChange={setTab}
          options={[{ value: "marketplace", label: "Marketplace" }, { value: "sessions", label: "Sessions" }]}
          testid="tx-tab"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: 8, borderRadius: 8 }} data-testid="tx-status">
          <option value="">All status</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="refunded">Refunded</option>
          <option value="payment_failed">Failed</option>
        </select>
      </Card>
      {rows.length === 0 ? <EmptyState title="No transactions" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>{tab === "marketplace" ? "Order" : "Booking"}</Th>
                <Th>{tab === "marketplace" ? "Buyer" : "Trainer"}</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>Razorpay ID</Th>
                <Th>Created</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.order_id || t.booking_id}>
                  <Td><code style={{ fontSize: 11 }}>{(t.order_id || t.booking_id || "").slice(0, 14)}…</code></Td>
                  <Td style={{ fontSize: 12 }}>{(tab === "marketplace" ? t.buyer_id : t.trainer_id || "")?.slice(0, 12)}</Td>
                  <Td>{fmtINR(t.amount_inr)}</Td>
                  <Td><StatusBadge value={t.status} /></Td>
                  <Td style={{ fontSize: 11, fontFamily: "monospace" }}>{t.razorpay_payment_id || "—"}</Td>
                  <Td>{relTime(t.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function RevenuePayouts() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const load = useCallback(() => {
    apiClient.get("/superadmin/revenue/payouts").then((r) => { setRows(r.data?.payouts || []); setSelected(new Set()); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const togg = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const markPaid = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Mark ${selected.size} trainer payout(s) as paid?`)) return;
    try {
      const { data } = await apiClient.post("/superadmin/revenue/payouts/mark-paid", { trainer_ids: [...selected] });
      toast.success(`Updated ${data.updated} bookings`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const totalPayout = rows.filter((r) => selected.has(r.trainer_id)).reduce((s, r) => s + (r.payout || 0), 0);

  return (
    <div className="space-y-4" data-testid="revenue-payouts">
      <SectionHeader title="Trainer Payouts" onRefresh={load} />
      {rows.length === 0 ? <EmptyState title="No pending payouts" sub="All completed sessions are paid up." /> : (
        <>
          <Card style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 13 }}>
              Selected: <strong>{selected.size}</strong> trainers · Total payout: <strong>{fmtINR(totalPayout)}</strong>
            </div>
            <PrimaryBtn onClick={markPaid} disabled={!selected.size} data-testid="payouts-mark-paid">Mark Paid</PrimaryBtn>
          </Card>
          <TableShell>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <Th>
                    <input type="checkbox" checked={selected.size === rows.length && rows.length > 0}
                      onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.trainer_id)) : new Set())}
                      data-testid="payout-select-all" />
                  </Th>
                  <Th>Trainer</Th><Th>UPI ID</Th><Th>Sessions</Th><Th>Gross</Th><Th>Commission</Th><Th>Payout</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.trainer_id}>
                    <Td><input type="checkbox" checked={selected.has(r.trainer_id)} onChange={() => togg(r.trainer_id)} data-testid={`payout-cb-${r.trainer_id}`} /></Td>
                    <Td><strong>{r.trainer_name}</strong></Td>
                    <Td style={{ fontSize: 12 }}>{r.upi_id || <span style={{ color: "var(--error, #EF4444)" }}>not set</span>}</Td>
                    <Td>{r.sessions}</Td>
                    <Td>{fmtINR(r.gross)}</Td>
                    <Td>{fmtINR(r.commission)}</Td>
                    <Td><strong>{fmtINR(r.payout)}</strong></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableShell>
        </>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function RevenueMRR() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/revenue/mrr").then((r) => setData(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="revenue-mrr">
      <SectionHeader title="MRR & ARPU" onRefresh={load} />
      {!data ? <EmptyState title="Loading…" /> : (
        <SummaryTiles tiles={[
          { label: "MRR (last 30d)", value: fmtINR(data.mrr) },
          { label: "ARPU", value: fmtINR(data.arpu) },
          { label: "Annual Run-rate", value: fmtINR(data.run_rate) },
          { label: "Rev / Session", value: fmtINR(data.rev_per_session) },
        ]} />
      )}
      {data && (
        <Card>
          <div style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)" }}>
            Computed across <strong>{data.mau}</strong> MAUs (active in last 30 days).
            <br />MRR = trailing 30-day captured payments. Run-rate = annualised projection from this month-to-date.
          </div>
        </Card>
      )}
    </div>
  );
}
